"use client";

import { useCallback, useEffect, useId, useRef, useState, type ReactNode } from "react";
import { ArrowLeft, Search } from "lucide-react";
import { ApiError, api, commandId, reasonText } from "../../lib/api";
import { Banner, Button, Field, Input, PageHeader } from ".";
import { useUnsavedWork } from "./unsaved-work";

export type PageResult<T> = { items: T[]; meta?: { nextCursor?: string } };
export function useRead<T>(path: string | null) {
  const [data, setData] = useState<T | null>(null),
    [error, setError] = useState("");
  const [loading, setLoading] = useState(!!path);
  const request = useRef(0);
  const [resolvedPath, setResolvedPath] = useState<string | null>(path);
  const reload = useCallback(async () => {
    const ticket = ++request.current;
    setResolvedPath(path);
    setData(null);
    setError("");
    setLoading(!!path);
    if (!path) return;
    try {
      const result = await api<T>(path);
      if (ticket === request.current) setData(result);
    } catch (caught) {
      if (ticket === request.current) setError(reasonText(caught));
    } finally {
      if (ticket === request.current) setLoading(false);
    }
  }, [path]);
  useEffect(() => {
    void reload();
    return () => {
      request.current++;
    };
  }, [reload]);
  return {
    data: resolvedPath === path ? data : null,
    error: resolvedPath === path ? error : "",
    loading: resolvedPath === path ? loading : !!path,
    reload
  };
}
export function ReadState({
  loading,
  error,
  retry
}: {
  loading: boolean;
  error: string;
  retry: () => void;
}) {
  if (error)
    return (
      <div className="workspace-error" role="alert">
        <span>{error}</span>
        <Button type="button" variant="outline" onClick={retry}>
          重新读取
        </Button>
      </div>
    );
  if (loading)
    return (
      <p className="record-loading" role="status">
        正在读取…
      </p>
    );
  return null;
}
export type Option = { value: string; label: string };
export function Choice({
  label,
  value,
  onChange,
  options,
  empty = "请选择",
  required = false,
  disabled = false
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: Option[];
  empty?: string;
  required?: boolean;
  disabled?: boolean;
}) {
  return (
    <Field label={label}>
      <select
        className="record-select"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        disabled={disabled}
      >
        <option value="">{empty}</option>
        {options.map((item) => (
          <option key={item.value} value={item.value}>
            {item.label}
          </option>
        ))}
      </select>
    </Field>
  );
}
const BLOCKERS: Record<string, string> = {
  DOMAIN_ROOT: "域根组织不能归档",
  ACTIVE_CHILDREN: "仍有在用下级组织",
  ACTIVE_MEMBERSHIPS: "仍有有效会籍",
  LIVE_SCOPE_GRANTS: "仍有有效授权",
  OPEN_PROFILE_CASES: "仍有未办结资料变更",
  OPEN_CLUE_SUBMISSIONS: "仍有未办结建言",
  OPEN_COLLECTION_TASKS: "仍有征集任务",
  OPEN_ACTIVITIES: "仍有未结束活动",
  ALREADY_MERGED: "已经并入其他档案",
  ACTIVE_ACCOUNT_LINK: "仍有账号绑定",
  HAS_CLUES: "关联建言记录",
  HAS_PROFILE_CASES: "关联资料变更办理",
  HAS_NOTIFICATIONS: "关联通知收件",
  HAS_ACTIVITY_REGISTRATIONS: "关联活动报名"
};
export function CommandForm({
  eyebrow = "档案与组织 · 办理",
  title,
  description,
  dirty,
  children,
  onCancel,
  onSubmit,
  onSaved,
  label = "保存变更",
  danger = false,
  disabled = false
}: {
  title: string;
  description: string;
  dirty: boolean;
  children: ReactNode;
  onCancel: () => void;
  onSubmit: (send: <T>(path: string, body: object) => Promise<T>) => Promise<unknown>;
  onSaved: (result: unknown) => void;
  eyebrow?: string;
  label?: string;
  danger?: boolean;
  disabled?: boolean;
}) {
  const [busy, setBusy] = useState(false),
    [error, setError] = useState("");
  const lock = useRef(false),
    command = useRef<{ fingerprint: string; id: string } | null>(null);
  const guard = useUnsavedWork(dirty, busy);
  return (
    <div className="record-editor">
      <Button variant="ghost" className="record-back" onClick={() => guard.requestLeave(onCancel)}>
        <ArrowLeft size={16} />
        返回
      </Button>
      <PageHeader eyebrow={eyebrow} title={title} description={description} />
      <form
        onSubmit={async (event) => {
          event.preventDefault();
          if (lock.current || disabled) return;
          lock.current = true;
          setBusy(true);
          setError("");
          try {
            const result = await onSubmit(async <T,>(path: string, body: object): Promise<T> => {
              const fingerprint = JSON.stringify({ path, body });
              if (command.current?.fingerprint !== fingerprint)
                command.current = { fingerprint, id: commandId() };
              return api<T>(path, {
                method: "POST",
                body: { ...body, commandId: command.current.id }
              });
            });
            // The mutation succeeded. Reading the refreshed page is a separate operation.
            onSaved(result);
          } catch (caught) {
            setError(
              caught instanceof ApiError && caught.blockers.length
                ? `${reasonText(caught)}：${caught.blockers.map((b) => BLOCKERS[b] ?? b).join("；")}`
                : reasonText(caught)
            );
          } finally {
            lock.current = false;
            setBusy(false);
          }
        }}
      >
        <fieldset disabled={busy} className="record-fields">
          {children}
        </fieldset>
        {error && <Banner tone="danger">{error}</Banner>}
        <div className="record-editor-footer">
          <span>{busy ? "正在提交，请稍候" : "办理结果记入操作记录"}</span>
          <div className="workspace-actions">
            <Button
              type="button"
              variant="outline"
              disabled={busy}
              onClick={() => guard.requestLeave(onCancel)}
            >
              取消
            </Button>
            <Button
              type="submit"
              variant={danger ? "destructive" : "default"}
              disabled={busy || disabled}
            >
              {busy ? "正在提交…" : label}
            </Button>
          </div>
        </div>
      </form>
      {guard.dialog}
    </div>
  );
}
export function Evidence({
  values,
  change
}: {
  values: { sourceRef: string; reason: string };
  change: (key: "sourceRef" | "reason", value: string) => void;
}) {
  return (
    <section className="record-section">
      <h2>办理依据</h2>
      <Field label="来源依据" hint="填写表单、文件或核对记录的名称与编号。">
        <Input
          required
          minLength={2}
          maxLength={200}
          value={values.sourceRef}
          onChange={(e) => change("sourceRef", e.target.value)}
        />
      </Field>
      <Field label="办理理由">
        <Input
          required
          minLength={4}
          maxLength={500}
          value={values.reason}
          onChange={(e) => change("reason", e.target.value)}
        />
      </Field>
    </section>
  );
}
export type PersonOption = { personId: string; displayName: string; organizationName: string };
export function PersonPicker({
  endpoint,
  value,
  onChange,
  exclude,
  initialQuery = ""
}: {
  endpoint: string;
  value: PersonOption | null;
  onChange: (item: PersonOption | null) => void;
  exclude?: string;
  /** 打开时先按这个姓名查一次（账号激活里带会员自填的姓名）。 */
  initialQuery?: string;
}) {
  const [word, setWord] = useState(initialQuery),
    [query, setQuery] = useState(initialQuery),
    [cursor, setCursor] = useState("");
  // 一页上可能同时开着几个查找（账号激活里每张「对不上」的单一个），标签与输入框的关联不能撞号。
  const searchId = useId();
  const result = useRead<PageResult<PersonOption>>(
    `${endpoint}?${new URLSearchParams({ q: query, limit: "10", ...(cursor ? { cursor } : {}) })}`
  );
  return (
    <div className="person-picker">
      <label className="record-label" htmlFor={searchId}>
        查找会员
      </label>
      <div className="record-search">
        <Input
          id={searchId}
          value={word}
          onChange={(e) => setWord(e.target.value)}
          placeholder="输入姓名"
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              setQuery(word.trim());
              setCursor("");
            }
          }}
        />
        <Button
          type="button"
          variant="outline"
          onClick={() => {
            setQuery(word.trim());
            setCursor("");
          }}
        >
          <Search size={16} />
          查找
        </Button>
      </div>
      <ReadState {...result} retry={result.reload} />
      {value && (
        <div className="record-selection">
          已选择：{value.displayName} · {value.organizationName}
          <Button type="button" variant="ghost" onClick={() => onChange(null)}>
            重新选择
          </Button>
        </div>
      )}
      {!result.loading && !result.error && (
        <ul className="person-options">
          {result.data?.items
            .filter((p) => p.personId !== exclude)
            .map((p) => (
              <li key={p.personId}>
                <Button
                  type="button"
                  variant="ghost"
                  aria-pressed={value?.personId === p.personId}
                  onClick={() => onChange(p)}
                >
                  <span>{p.displayName}</span>
                  <span>{p.organizationName}</span>
                </Button>
              </li>
            ))}
        </ul>
      )}
      {result.data?.items.filter((p) => p.personId !== exclude).length === 0 && (
        <p className="record-muted">没有匹配会员，请调整关键词。</p>
      )}
      <div className="workspace-actions">
        {cursor && (
          <Button type="button" variant="outline" onClick={() => setCursor("")}>
            回到首批
          </Button>
        )}
        {result.data?.meta?.nextCursor && (
          <Button
            type="button"
            variant="outline"
            onClick={() => setCursor(result.data!.meta!.nextCursor!)}
          >
            下一批
          </Button>
        )}
      </div>
    </div>
  );
}
